import { useState, useEffect, Fragment, memo } from "react";
import {
  FormControl,
  FormHelperText,
  TextField,
  Chip,
  Box,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import PropTypes from "prop-types";
import {
  getAPIEndpoint,
  handleAPICall,
  convertToDefEventPara,
} from "../helper.js";
import DocumentViewer from "../Viewer/DocumentViewer.jsx";
import { useSnackbar } from "notistack";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { componentTokens } from "@accredo/fe-shared/theme/tokens/components.js";

AutoCompleteComponent.propTypes = {
  item: PropTypes.object.isRequired,
  value: PropTypes.any,
  error: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  showErrorText: PropTypes.bool,
  disabled: PropTypes.bool,
  apiGet: PropTypes.func,
  dependencyValue: PropTypes.any,
};

// V2 styling based on Figma design tokens
const getV2Styles = (hasError) => {
  const tokens = componentTokens.input;
  return {
    "& .MuiOutlinedInput-root": {
      minHeight: tokens.height,
      borderRadius: tokens.borderRadius,
      backgroundColor: tokens.colors.background,
      fontSize: tokens.fontSize,
      "& fieldset": {
        borderColor: hasError
          ? tokens.colors.border.error
          : tokens.colors.border.default,
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: hasError
          ? tokens.colors.border.error
          : tokens.colors.border.hover,
      },
      "&.Mui-focused fieldset": {
        borderColor: hasError
          ? tokens.colors.border.error
          : tokens.colors.border.focus,
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: hasError ? tokens.colors.label.error : tokens.colors.placeholder,
      "&.MuiInputLabel-shrink": {
        color: hasError ? tokens.colors.label.error : tokens.colors.label.default,
        fontSize: tokens.label.fontSize,
      },
      "&.Mui-focused": {
        color: hasError ? tokens.colors.label.error : tokens.colors.label.focus,
        fontSize: tokens.label.fontSize,
      },
    },
    "& .MuiFormHelperText-root": {
      color: hasError
        ? tokens.colors.helperText.error
        : tokens.colors.helperText.default,
    },
    "& .MuiOutlinedInput-input": {
      color: tokens.colors.text,
      "&::placeholder": {
        color: tokens.colors.placeholder,
        opacity: 1,
      },
    },
  };
};

function AutoCompleteComponent(props) {
  const {
    item,
    value,
    values,
    error = null,
    onChange,
    showErrorText = false,
    disabled = false,
    apiGet = null,
    dependencyValue = null,
  } = props;
  const [isLoading, setIsLoading] = useState(!!(item.get_api && !item.options));
  const [options, setOptions] = useState(item.options || null);
  const [lastAPIEndpoint, setLastAPIEndpoint] = useState("");
  const [documentToView, setDocumentToView] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const get_api =
      typeof item.get_api === "function" ? item.get_api(values) : item.get_api;

    const fetchData = async () => {
      const endpoint = await handleAPICall(
        get_api,
        values,
        apiGet,
        setOptions,
        setIsLoading
      );
      setLastAPIEndpoint(endpoint);
    };

    const currentEndpoint = getAPIEndpoint(get_api, values);
    if (get_api && lastAPIEndpoint !== currentEndpoint) {
      fetchData();
    } else if (!get_api) {
      setIsLoading(false);
    }
  }, [dependencyValue]);

  const handleOnChange = (_, newValue) => {
    if (item.write_label_to) {
      onChange([
        convertToDefEventPara(item.write_label_to, newValue?.label || null),
        convertToDefEventPara(item.name, newValue?.id || null),
      ]);
    } else {
      onChange(convertToDefEventPara(item.name, newValue?.id || null));
    }
  };

  const handleOnChange1 = (_, newValue) => {
    if (item.write_label_to) {
      onChange([
        convertToDefEventPara(item.write_label_to, newValue?.label || null),
        convertToDefEventPara(item.name, newValue?.id || null),
      ]);
    } else {
      onChange(convertToDefEventPara(item.name, newValue || null));
    }
  };

  const handleChipClick = (option) => {
    if (item.canPreview) {
      backendGet(`data/anlages/${option.id}`, {
        label: "Dokument",
        setIsLoading: setIsLoading,
        setResult: processDocumentToView,
        enqueueSnackbar: enqueueSnackbar,
      });
    }
  };
  const processDocumentToView = (data) => {
    setDocumentToView({
      filedata: data.filedata,
      mimetype: data.mimetype,
      success: true,
      filetype: data.filetype,
      filename: data.filename,
    });
  };

  let helperText = "";
  if (showErrorText) helperText = error;

  const RenderControl = () => {
    if (isLoading) return <></>;
    else {
      if (disabled) {
        if (Array.isArray(value))
          return (
            <Box>
              {item.label}
              {value?.map((option, index) => (
                <Chip
                  key={index}
                  label={option.label}
                  onClick={(e) => handleChipClick(option)}
                ></Chip>
              ))}
            </Box>
          );
        else return <></>;
      } else {
        if (item.multiple) {
          let acvalue = undefined;
          if (value)
            acvalue = value.filter((itemA) =>
              options.some((itemB) => itemA.id === itemB.id)
            );

          return (
            <FormControl
              {...(error && { error: true })}
              style={{ width: "100%" }}
              sx={getV2Styles(!!error)}
            >
              {options && (
                <Autocomplete
                  value={acvalue || []}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  multiple={true}
                  options={options}
                  disabled={disabled}
                  noOptionsText="Keine Optionen gefunden"
                  filterSelectedOptions
                  getOptionLabel={(option) => option.label}
                  onChange={handleOnChange1}
                  renderInput={(params) => (
                    <TextField
                      label={item.label + " auswählen"}
                      variant="outlined"
                      size="medium"
                      {...params}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.label}
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={index}
                        label={option.label}
                        onClick={() => handleChipClick(option)}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                />
              )}
              <FormHelperText>{helperText}</FormHelperText>
            </FormControl>
          );
        } else {
          return (
            <FormControl
              {...(error && { error: true })}
              style={{ width: "100%" }}
              sx={getV2Styles(!!error)}
            >
              {options && (
                <Autocomplete
                  value={options.find((option) => option.id === value) || []}
                  multiple={false}
                  options={options}
                  disabled={disabled}
                  noOptionsText="Keine Optionen gefunden"
                  filterSelectedOptions
                  getOptionLabel={(option) => option.label || "bitte wählen"}
                  onChange={handleOnChange}
                  renderInput={(params) => (
                    <TextField
                      label={item.label + " auswählen"}
                      variant="outlined"
                      size="medium"
                      {...params}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.label}
                    </li>
                  )}
                />
              )}
              <FormHelperText>{helperText}</FormHelperText>
            </FormControl>
          );
        }
      }
    }
  };

  return (
    <Fragment>
      <RenderControl />
      <DocumentViewer document={documentToView} />
    </Fragment>
  );
}

const AutoComplete = memo(
  AutoCompleteComponent,
  (prev, next) =>
    prev.value === next.value &&
    prev.dependencyValue === next.dependencyValue &&
    prev.disabled === next.disabled &&
    prev.error === next.error &&
    prev.showErrorText === next.showErrorText &&
    prev.item === next.item
);

export default AutoComplete;
